import { Button, FormHelperText, Paper, Stack, Typography } from "@mui/material";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import HiddenFileInput from "@entrilia/shared/components/inputs/HiddenFileInput";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { InvestorCommunicationType } from "../../../../../api/types/investorCommunicationsTypes";
import type { CommunicationImageSource } from "../investorCommunicationDetailsTypes";
import InvestorCommunicationPreviewImage from "./InvestorCommunicationPreviewImage";
import { acceptedImageFileExtensions, isAcceptedImageFile, maxHeroImageSizeBytes } from "./investorCommunicationFiles";

interface Props {
  title: string;
  hint: string;
  type: InvestorCommunicationType;
  source?: CommunicationImageSource;
  error?: string;
  disabled?: boolean;
  progressText?: string;
  onFileAdd: (file: File) => void;
  onRemove?: () => void;
}

const InvestorCommunicationImageField = ({
  title,
  hint,
  type,
  source,
  error,
  disabled,
  progressText,
  onFileAdd,
  onRemove,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const handleFilesAdd = (files: File[]) => {
    const file = files[0];
    if (file === undefined) {
      return;
    }

    if (!isAcceptedImageFile(file)) {
      sendNotificationError(`Unsupported image format. Allowed: ${acceptedImageFileExtensions.join(", ")}`);
      return;
    }

    if (file.size > maxHeroImageSizeBytes) {
      sendNotificationError("The image is too large. Maximum size is 25 MB.");
      return;
    }

    onFileAdd(file);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">{title}</Typography>

      {source ? (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <InvestorCommunicationPreviewImage type={type} source={source} />
          <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
            <Typography color="textSecondary">{hint}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Button variant="outlined" color="secondary" component="label" disabled={disabled}>
                Replace
                <HiddenFileInput acceptedFileTypes={acceptedImageFileExtensions} onFilesAdd={handleFilesAdd} />
              </Button>
              {onRemove && (
                <Button variant="text" color="error" disabled={disabled} onClick={onRemove}>
                  Remove
                </Button>
              )}
            </Stack>
          </Stack>
        </Stack>
      ) : progressText ? (
        <Paper variant="outlined" sx={{ display: "flex", py: 3 }}>
          <InlineLoader text={progressText} />
        </Paper>
      ) : (
        <FilesDropArea
          acceptedFileExtensions={acceptedImageFileExtensions}
          subtitle={hint}
          disabled={disabled}
          onFilesAdd={handleFilesAdd}
        />
      )}

      {error && <FormHelperText error>{error}</FormHelperText>}
    </Stack>
  );
};

export default InvestorCommunicationImageField;
